import React, { useEffect, useRef, useState } from "react";

const inputStyle: React.CSSProperties = {
  padding: "0.75rem",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  backgroundColor: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "var(--font-body)",
  width: "100%",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  marginBottom: "0.5rem",
  fontWeight: "600",
  display: "block",
};

/**
 * Number input. By default, when value is 0 we show empty string to avoid "012"
 * when typing. Use showZero={true} to display "0" (e.g. for Aantal in dossier).
 *
 * Sinds 02-09-2026 (besluit D4) een tekstveld met decimaal toetsenbord: komma
 * én punt worden geaccepteerd ("1,5" → 1.5). Een `type="number"` gaf bij een
 * komma een lege waarde terug, waardoor uren als 0 werden opgeslagen.
 * `min`/`max` worden bij het verlaten van het veld afgedwongen (clampen);
 * `step` blijft alleen bestaan zodat bestaande aanroepen compileren.
 *
 * Bij focus wordt de hele waarde geselecteerd, zodat typen haar vervangt
 * (livegang 27-09). Een tweede klik zet de cursor gewoon.
 */
export interface ModalNumberFieldProps {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  /** Geen werking meer (tekstveld zonder spinner); blijft voor bestaande aanroepen. */
  step?: number | "any";
  required?: boolean;
  /** If true, empty input is sent as 0. If false, as undefined. Default true. */
  allowZero?: boolean;
  /** If true, display "0" when value is 0 instead of empty. Default false. */
  showZero?: boolean;
  id?: string;
  /** Optional style for the wrapper div (e.g. gridColumn for grid layouts) */
  wrapperStyle?: React.CSSProperties;
  /** When true and label is empty, hide the label to save space (e.g. inline use) */
  compact?: boolean;
  /** Optionele ref op het invoerveld (bijv. om er programmatisch op te focussen). */
  inputRef?: React.Ref<HTMLInputElement>;
}

/** Optioneel minteken, cijfers, hooguit één komma of punt. */
const TOEGESTANE_INVOER = /^-?\d*[.,]?\d*$/;

function naarTekst(value: number | undefined, showZero: boolean): string {
  if (value === undefined || value === null) return "";
  if (value === 0) return showZero ? "0" : "";
  return String(value);
}

/** `null` = nog geen cijfer ingevoerd ("", "-", ",", "-."). */
function naarGetal(tekst: string): number | null {
  const genormaliseerd = tekst.replace(",", ".");
  if (!/\d/.test(genormaliseerd)) return null;
  const parsed = parseFloat(genormaliseerd);
  return Number.isNaN(parsed) ? null : parsed;
}

export function ModalNumberField({
  label,
  value,
  onChange,
  min,
  max,
  required,
  allowZero = true,
  showZero = false,
  id,
  wrapperStyle,
  compact,
  inputRef,
}: ModalNumberFieldProps) {
  const fieldId = id || `modal-number-${label.replace(/\s/g, "-")}`;

  // Eigen teksttoestand: een controlled tekstveld zou "1," direct terugzetten
  // naar "1" en dan is een komma niet te typen.
  const [tekst, setTekst] = useState(() => naarTekst(value, showZero));
  const vorigeWaarde = useRef(value);
  // De mouseup van de klik die het veld focust, heft de selectie anders op.
  const selecteerNaKlik = useRef(false);

  // Alleen een échte wijziging van buiten (nieuwe prop) overschrijft de invoer,
  // en dan nog alleen als de invoer niet al dezelfde waarde voorstelt.
  useEffect(() => {
    if (vorigeWaarde.current === value) return;
    vorigeWaarde.current = value;
    const huidig = naarGetal(tekst);
    const huidigAlsWaarde =
      huidig === null ? (allowZero ? 0 : undefined) : huidig;
    if (huidigAlsWaarde !== value) setTekst(naarTekst(value, showZero));
  }, [value, tekst, allowZero, showZero]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (!TOEGESTANE_INVOER.test(raw)) return;
    setTekst(raw);
    const getal = naarGetal(raw);
    onChange(getal === null ? (allowZero ? 0 : undefined) : getal);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
    selecteerNaKlik.current = true;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!selecteerNaKlik.current) return;
    e.preventDefault();
    selecteerNaKlik.current = false;
  };

  const handleBlur = () => {
    selecteerNaKlik.current = false;
    const getal = naarGetal(tekst);
    if (getal === null) return;
    let geklemd = getal;
    if (min !== undefined && geklemd < min) geklemd = min;
    if (max !== undefined && geklemd > max) geklemd = max;
    if (geklemd !== getal) {
      setTekst(String(geklemd));
      onChange(geklemd);
    }
  };

  const showLabel = !compact || (label && label.length > 0);
  return (
    <div className="modal-field" style={wrapperStyle}>
      {showLabel && (
        <label htmlFor={fieldId} style={labelStyle}>
          {label}
          {required && " *"}
        </label>
      )}
      <input
        id={fieldId}
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={tekst}
        onChange={handleChange}
        onFocus={handleFocus}
        onMouseUp={handleMouseUp}
        onBlur={handleBlur}
        required={required}
        style={inputStyle}
      />
    </div>
  );
}
